import React, { FC, useMemo } from 'react'
import { Link as RouteLink } from 'react-router-dom'
import { Box, Grid, Link, Typography } from '@mui/material'
import { Formik } from 'formik'
import { routes } from 'config'
import { IFormProps, isMultiLoginApp } from 'utils'
import {
  Module,
  ModuleHeader,
  ModuleBody,
  CustomAlert,
  CustomTextField,
  ButtonContainer,
  CustomButton,
  CustomCheckbox,
} from 'components'
import useStyles from './login-form-style'

const LoginForm: FC<IFormProps> = ({
  initialValues,
  validationSchema,
  handleSubmit,
  serviceError,
  serviceMessage,
  rememberMeValue,
  handleRemember,
  formikRef,
  errorDate,
  title,
  isImpersonateLogin,
  hiddeTitle,
}) => {
  const { classes } = useStyles()
  const titleToShow = useMemo(() => title || 'Log in to Bright MLS', [title])
  const impersonateLogin = useMemo(
    () => isImpersonateLogin || process.env.REACT_APP_IMPERSONATE_LOGIN,
    [isImpersonateLogin],
  )

  return (
    <Module>
      {serviceMessage && (
        <CustomAlert
          severity="success"
          title={isMultiLoginApp() ? 'Multi Login Password Updated' : 'Success'}
        >
          {serviceMessage}
        </CustomAlert>
      )}
      <CustomAlert severity="error">{serviceError}</CustomAlert>
      <ModuleHeader title={!hiddeTitle ? titleToShow : ''}></ModuleHeader>

      {isMultiLoginApp() && (
        <p style={{ marginTop: '-1rem' }}>
          Log in with your Multi Login account to manage all associated accounts
          in one place.
        </p>
      )}

      <ModuleBody isNewLayout>
        <Formik
          innerRef={formikRef}
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({
            handleSubmit: onSubmit,
            dirty,
            isValid,
            isSubmitting,
            getFieldProps,
            getFieldMeta,
            setFieldTouched,
            setFieldError,
            touched,
            errors,
            values,
          }) => (
            <form onSubmit={onSubmit}>
              <CustomTextField
                autoFocus
                id="username"
                label=""
                placeholder={
                  isMultiLoginApp()
                    ? 'Enter your multi login name'
                    : 'Enter your login name'
                }
                hasServiceError={Boolean(serviceError)}
                touched={touched}
                errors={errors}
                values={values}
                getFieldMeta={getFieldMeta}
                setFieldTouched={setFieldTouched}
                setFieldError={setFieldError}
                fullWidth
                {...getFieldProps('username')}
              />

              <CustomTextField
                id="password"
                type="password"
                label=""
                placeholder="Enter your password"
                hasServiceError={Boolean(serviceError)}
                touched={touched}
                errors={errors}
                values={values}
                getFieldMeta={getFieldMeta}
                setFieldTouched={setFieldTouched}
                setFieldError={setFieldError}
                fullWidth
                {...getFieldProps('password')}
              />

              <Box marginBottom={7} marginTop={2}>
                <Grid container justifyContent="space-between">
                  <Grid item xs={5}>
                    <CustomCheckbox
                      checked={rememberMeValue}
                      onChange={handleRemember}
                      label="Remember me"
                      className={classes.loginCheckBox}
                    />
                  </Grid>
                  <Grid item xs={7}>
                    {impersonateLogin !== 'true' && (
                      <Typography
                        align="right"
                        className={classes.forgetUsernameTitle}
                      >
                        Forgot{' '}
                        <RouteLink to={routes.forgotLoginName}>
                          {isMultiLoginApp()
                            ? 'multi login name'
                            : 'login name'}
                        </RouteLink>{' '}
                        or{' '}
                        <RouteLink to={routes.forgotPassword}>
                          password
                        </RouteLink>
                        ?
                      </Typography>
                    )}
                  </Grid>
                </Grid>
              </Box>

              <ButtonContainer
                primary={
                  <CustomButton
                    type="submit"
                    disabled={!dirty || !isValid || isSubmitting}
                  >
                    LOG IN
                  </CustomButton>
                }
              />
            </form>
          )}
        </Formik>

        {impersonateLogin !== 'true' && (
          <Box
            display="flex"
            marginTop={8}
            marginBottom={4}
            className={classes.SubscriberAndJoinUSBOX}
          >
            <Box
              borderTop={1}
              borderBottom={1}
              paddingY={1.5}
              paddingRight={[1, 2.5]}
              className={classes.SubscriberBOX}
            >
              <Typography
                variant="h2"
                style={{ fontSize: '14px' }}
                className={classes.NotBrightMLSSubscriberTitle}
              >
                {isMultiLoginApp()
                  ? 'Want to use your regular login instead?'
                  : 'Not a Bright MLS Subscriber?'}
              </Typography>
            </Box>
            <Box
              borderTop={1}
              borderBottom={1}
              paddingY={1.5}
              className={classes.JoinUSBOX}
            >
              <Typography
                style={{ fontSize: '14px' }}
                variant="h2"
                className={classes.JoinUS}
              >
                <Link
                  target="_blank"
                  href={routes.BCOM.join}
                  className={classes.joinUsTitle}
                >
                  {isMultiLoginApp() ? 'Return to main login' : 'JOIN US'}
                </Link>
              </Typography>
            </Box>
          </Box>
        )}
      </ModuleBody>
    </Module>
  )
}

export default LoginForm
